import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { ReferralOptionsDropdown } from "@/referrals/common/referral-options-dropdown";
import { IReferral } from "@/referrals/referral";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwnerOrManager } from "@/user/utils";

interface ActionsColContentProps {
  referral: IReferral;
  setPayoutReferral: (referral: IReferral) => void;
  onLost: (referralId: string) => void;
  onMoveToPaid: (referralId: string) => void;
  onDelete: (referralId: string) => void;
}

const ActionsColContent = ({
  referral,
  setPayoutReferral,
  onLost,
  onMoveToPaid,
  onDelete,
}: ActionsColContentProps) => {
  const { data: userInfo } = useGetMyUser();
  const canPayout = isAdminOrOwnerOrManager(userInfo?.role);
  const handleLost = useCallback(() => {
    onLost(referral.id);
  }, [onLost, referral.id]);

  const handleMoveToPaid = useCallback(() => {
    onMoveToPaid(referral.id);
  }, [onMoveToPaid, referral.id]);

  const handleDelete = useCallback(() => {
    onDelete(referral.id);
  }, [onDelete, referral.id]);

  return (
    <Stack
      direction="row"
      justify="between"
      align="center"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip delayDuration={canPayout ? 10000 : 750}>
        <TooltipTrigger className="inline-block" asDiv>
          <Button
            variant="secondary"
            size="sm"
            className="w-[77px]"
            disabled={!canPayout}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!canPayout) {
                return;
              }
              setPayoutReferral(referral);
            }}
          >
            Pay
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          Only owners and managers can pay advocates for referrals.
        </TooltipContent>
      </Tooltip>
      <ReferralOptionsDropdown
        referral={referral}
        onLost={handleLost}
        onMoveToPaid={handleMoveToPaid}
        onDelete={handleDelete}
      >
        <EllipsisVerticalIcon className="h-6 w-6 min-w-6" />
      </ReferralOptionsDropdown>
    </Stack>
  );
};

interface ActionsColProps {
  setPayoutReferral: (referral: IReferral) => void;
  onLost: (referralId: string) => void;
  onMoveToPaid: (referralId: string) => void;
  onDelete: (referralId: string) => void;
}

const ActionsCol = ({
  setPayoutReferral,
  onLost,
  onMoveToPaid,
  onDelete,
}: ActionsColProps): ColumnDef<IReferral> => ({
  id: "actions",
  meta: {
    size: "130px",
  },
  cell: ({ row }) => (
    <ActionsColContent
      referral={row.original}
      setPayoutReferral={setPayoutReferral}
      onLost={onLost}
      onMoveToPaid={onMoveToPaid}
      onDelete={onDelete}
    />
  ),
  enableSorting: false,
  enableResizing: false,
  enablePinning: true,
});

export default ActionsCol;
